import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
  CSSProperties
} from 'react';
import {
  Edit3,
  Check,
  X,
  Trash2,
  Filter,
  ChevronUp,
  ChevronDown,
  History,
  Plus
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import {
  Row,
  ColumnFilter,
  SortState,
  Cell
} from '../types/spreadsheet';

import ConfirmationModal from './ConfirmationModal';
import CellHistoryModal   from './CellHistoryModal';

/* --------------------------------------------------------
 *  LINHA MEMOIZADA
 * ------------------------------------------------------ */
interface RowViewProps {
  row        : Row;
  rowIndex   : number;
  columns    : string[];
  onDelete   : (rowIdx: number) => void;
  onHistory  : (rowIdx: number, col: string, e: React.MouseEvent) => void;
  onUpdate   : (rowIdx: number, col: string, value: string) => void;
  onStartEdt : (rowIdx: number, col: string) => void;
  onStopEdt  : (rowIdx: number, col: string) => void;
  onRowContextMenu: (rowIdx: number, e: React.MouseEvent) => void;
  style?     : CSSProperties;        // para futura virtualização (opcional)
}

const RowView: React.FC<RowViewProps> = memo(
  ({
    row,
    rowIndex,
    columns,
    onDelete,
    onHistory,
    onUpdate,
    onStartEdt,
    onStopEdt,
    onRowContextMenu,
    style = {}
  }) => {
    const [editingCol, setEditingCol] = useState<string | null>(null);
    const [temp, setTemp]             = useState<string>('');

    /* ---- editar ------------------------------------------------------- */
    const startEdit = useCallback(
      (col: string) => {
        const cell = row[col] as Cell;
        setTemp(cell?.value ?? '');
        setEditingCol(col);
        onStartEdt(rowIndex, col);
      },
      [row, rowIndex, onStartEdt]
    );

    const confirm = useCallback(() => {
      if (editingCol === null) return;
      onUpdate(rowIndex, editingCol, temp);
      onStopEdt(rowIndex, editingCol);
      setEditingCol(null);
    }, [editingCol, temp, onUpdate, rowIndex, onStopEdt]);

    const cancel = useCallback(() => {
      if (editingCol === null) return;
      onStopEdt(rowIndex, editingCol);
      setEditingCol(null);
    }, [editingCol, rowIndex, onStopEdt]);

    /* ---- célula render ------------------------------------------------ */
    const renderCell = (col: string) => {
      const cell = row[col] as Cell;

      if (editingCol === col) {
        return (
          <div className="flex items-start gap-2">
            <textarea
              autoFocus
              value={temp}
              onChange={e => setTemp(e.target.value)}
              rows={Math.min(4, Math.max(1, Math.ceil(temp.length / 50)))}
              className="flex-1 p-2 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[40px]"
            />
            <div className="flex flex-col gap-1">
              <button
                onClick={confirm}
                title="Confirmar"
                className="p-1 text-green-600 hover:bg-green-100 rounded"
              >
                <Check size={16} />
              </button>
              <button
                onClick={cancel}
                title="Cancelar"
                className="p-1 text-red-600 hover:bg-red-100 rounded"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        );
      }

      return (
        <div
          onClick={() => startEdit(col)}
          className="p-2 min-h-[40px] cursor-pointer hover:bg-blue-50 rounded-md border border-transparent hover:border-blue-200 transition-all duration-150 group"
        >
          <div className="flex items-center gap-2">
            <span className="flex-1 whitespace-pre-wrap break-words text-sm">
              {cell?.value ?? ''}
            </span>
            <div className="flex items-center space-x-1 opacity-0 group-hover:!opacity-100 ml-2 transition-opacity">

              <button
                onClick={e => onHistory(rowIndex, col, e)}
                title="Ver histórico"
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
              >
                <History size={14} />
              </button>
              <Edit3 size={14} className="text-gray-400" />
            </div>
          </div>
        </div>
      );
    };

    return (
      <tr
        key={row.id}
        data-guid={row.id}
        style={style}
        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
      >
        <td 
          className="sticky left-0 z-10 p-4 text-center text-gray-500 font-medium bg-gray-50 cursor-context-menu hover:bg-gray-100 transition-colors border-r border-gray-200"
          onContextMenu={(e) => onRowContextMenu(rowIndex, e)}
          title="Clique com o botão direito para adicionar linha"
        >
          {rowIndex + 1}
        </td>

        {columns.map(col => (
          <td key={col} className="p-2 relative">
            {renderCell(col)}
          </td>
        ))}

        <td className="p-4 text-center">
          <button
            onClick={() => onDelete(rowIndex)}
            title="Excluir linha"
            className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
          >
            <Trash2 size={16} />
          </button>
        </td>
      </tr>
    );
  },
  (prev, next) =>
    // Só re-renderiza se o objeto da linha ou colunas mudarem
    prev.row === next.row && prev.columns === next.columns
);

/* --------------------------------------------------------
 *  TABELA
 * ------------------------------------------------------ */
interface SpreadsheetTableProps {
  columns        : string[];
  filteredRows   : Row[];
  originalRows   : Row[];
  columnFilters  : ColumnFilter;
  activeFilters  : Record<string, string>;
  sortState      : SortState;
  onSort         : (col: string) => void;
  onFilterChange : (col: string, value: string) => void;
  onClearFilter  : (col: string) => void;
  onStartEditing : (rowIdx: number, col: string) => void;
  onStopEditing  : (rowIdx: number, col: string) => void;
  onUpdateCell   : (rowIdx: number, col: string, value: string) => void;
  onDeleteRow    : (rowIdx: number) => void;
  onInsertRow    : (index: number) => void;
  hasActiveFiltersOrSearch: boolean;
}

const SpreadsheetTable: React.FC<SpreadsheetTableProps> = ({
  columns,
  filteredRows,
  originalRows,
  columnFilters,
  activeFilters,
  sortState,
  onSort,
  onFilterChange,
  onClearFilter,
  onStartEditing,
  onStopEditing,
  onUpdateCell,
  onDeleteRow,
  onInsertRow,
  hasActiveFiltersOrSearch
}) => {
  /* --------------- garante GUID em todas as linhas ------------------ */
  useEffect(() => {
    originalRows.forEach((row: any) => {
      // Caso o dado venha sem coluna GUID, criamos.
      if (!row.GUID) {
        const guid = uuidv4();
        row.GUID = { value: guid, history: [] };
        // Também preenche o campo id (chave já usada no componente)
        if (!row.id) row.id = guid;
      } else if (!row.id) {
        // Sincroniza id com GUID existente se id ainda vazio
        console.log(row.GUID);
        row.id = row.GUID;//(row.GUID as Cell).value;
      }
    });
  }, [originalRows]);

  /* --------------- coluna GUID sempre presente ---------------------- */
  const allColumns = useMemo<string[]>(() => {
    const exists = columns.some(c => c.toLowerCase() === 'guid');
    return exists ? columns : ['GUID', ...columns];
  }, [columns]);

  /* --------------- visibilidade ------------------------------------- */
  const visibleColumns = useMemo<string[]>(() =>
    allColumns.filter(c => c.toLowerCase() !== 'guid'), [allColumns]);

  /* --------------- dropdown / modal states --------------------------- */
  const [showDropdown , setShowDropdown ] = useState<string | null>(null);
  const [dropdownRect , setDropdownRect ] = useState<DOMRect | null>(null);
  const [delModal     , setDelModal     ] = useState<{open: boolean; row: number | null}>({open:false,row:null});
  const [histModal    , setHistModal    ] = useState<{open:boolean; row:number|null; col:string|null}>({open:false,row:null,col:null});
  
  /* --------------- context menu states ------------------------------- */
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuRowIndex, setContextMenuRowIndex] = useState<number | null>(null);
  const [showCannotAddRowModal, setShowCannotAddRowModal] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  /* --------------- map id -> originalIndex O(1) ---------------------- */
  const indexMap = useMemo(() => {
    const m = new Map<string | number, number>();
    originalRows.forEach((r, i) => m.set(r.id, i));
    return m;
  }, [originalRows]);

  /* --------------- eventos globais p/ dropdown ----------------------- */
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(null);
        setDropdownRect(null);
      }
    };
    const closeScroll = () => showDropdown && setShowDropdown(null);
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeScroll, true);
    window.addEventListener('resize', closeScroll);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeScroll, true);
      window.removeEventListener('resize', closeScroll);
    };
  }, [showDropdown]);

  /* --------------- eventos globais p/ context menu ------------------ */
  useEffect(() => {
    const closeContextMenu = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenuVisible(false);
        setContextMenuRowIndex(null);
      }
    };

    if (contextMenuVisible) {
      document.addEventListener('mousedown', closeContextMenu);
      return () => document.removeEventListener('mousedown', closeContextMenu);
    }
  }, [contextMenuVisible]);

  /* --------------- ícone de ordenação -------------------------------- */
  const sortIcon = (col: string) => {
    if (sortState.column !== col) return null;
    return sortState.direction === 'asc'
      ? <ChevronUp size={14} />
      : <ChevronDown size={14} />;
  };

  /* --------------- clique filtro ------------------------------------- */
  const openFilter = (col: string, e: React.MouseEvent<HTMLButtonElement>) => {
    if (showDropdown === col) {
      setShowDropdown(null);
      setDropdownRect(null);
    } else {
      setShowDropdown(col);
      setDropdownRect(e.currentTarget.getBoundingClientRect());
    }
  };

  /* --------------- posição dropdown ---------------------------------- */
  const dropdownStyle: React.CSSProperties = useMemo(() => {
    if (!dropdownRect) return {};
    const vw  = window.innerWidth;
    const w   = 256;           // .w-64
    let left  = dropdownRect.left;
    if (left + w > vw - 16) left = dropdownRect.right - w;
    if (left < 16) left = Math.max(16, dropdownRect.left + dropdownRect.width/2 - w/2);
    return {
      position:'fixed',
      top : dropdownRect.bottom + 8,
      left,
      zIndex: 9999
    };
  }, [dropdownRect]);

  /* --------------- handlers memoizados ------------------------------- */
  const handleHistory = useCallback(
    (row: number, col: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setHistModal({open:true,row,col});
    }, []);

  const handleDelete = useCallback((row: number) => {
    setDelModal({open:true,row});
  }, []);

  const handleRowContextMenu = useCallback((rowIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    
    // Verificar se há filtros ativos ou busca
    if (hasActiveFiltersOrSearch) {
      setShowCannotAddRowModal(true);
      return;
    }
    
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuRowIndex(rowIndex);
    setContextMenuVisible(true);
  }, [hasActiveFiltersOrSearch]);

  const handleInsertRowAbove = useCallback(() => {
    if (contextMenuRowIndex !== null) {
      onInsertRow(contextMenuRowIndex);
      setContextMenuVisible(false);
      setContextMenuRowIndex(null);
    }
  }, [contextMenuRowIndex, onInsertRow]);

  const confirmDelete = () => {
    if (delModal.row !== null) onDeleteRow(delModal.row);
    setDelModal({open:false,row:null});
  };

  /* ------------------------------------------------------------------- */
  return (
    <>
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden relative">
        {/* ----------- Cabeçalho -------------------------------------- */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gradient-to-r from-blue-600 to-blue-700">
                <th className="sticky left-0 z-20 w-16 p-4 text-white font-semibold text-center bg-blue-600 border-r border-blue-500">#</th>
                {visibleColumns.map(col => (
                  <th key={col} className="p-4 text-left text-white font-semibold min-w-48 relative">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onSort(col)}
                        className="flex-1 flex items-center gap-2 hover:bg-blue-500 px-2 py-1 rounded"
                      >
                        <span>{col}</span>
                        {sortIcon(col)}
                      </button>
                      <button
                        onClick={e => openFilter(col, e)}
                        title="Filtrar"
                        className={`p-1 rounded hover:bg-blue-500 ${activeFilters[col] ? 'bg-blue-500' : ''}`}
                      >
                        <Filter size={14} />
                      </button>
                    </div>
                  </th>
                ))}
                <th className="w-20 p-4 text-white font-semibold text-center">Ações</th>
              </tr>
            </thead>

            {/* ----------- Corpo com linhas memoizadas ---------------- */}
            <tbody>
              {filteredRows.map(row => {
                const originalIdx = indexMap.get(row.id)!;
                return (
                  <RowView
                    key={row.id}
                    row={row}
                    rowIndex={originalIdx}
                    columns={visibleColumns}
                    onDelete={handleDelete}
                    onHistory={handleHistory}
                    onUpdate={onUpdateCell}
                    onStartEdt={onStartEditing}
                    onStopEdt={onStopEditing}
                    onRowContextMenu={handleRowContextMenu}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ----------- Dropdown Filtro ------------------------------ */}
        {showDropdown && dropdownRect && (
          <div
            ref={dropdownRef}
            className="w-64 bg-white rounded-lg shadow-xl border border-gray-200 max-h-64 overflow-y-auto"
            style={dropdownStyle}
          >
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-medium text-gray-900">
                Filtrar por {showDropdown}
              </span>
              {activeFilters[showDropdown] && (
                <button
                  onClick={() => {
                    onClearFilter(showDropdown);
                    setShowDropdown(null);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Limpar
                </button>
              )}
            </div>

            <div className="max-h-48 overflow-y-auto">
              <button
                onClick={() => {
                  onFilterChange(showDropdown, '');
                  setShowDropdown(null);
                }}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${
                  !activeFilters[showDropdown] ? 'bg-blue-50 text-blue-700' : ''
                }`}
              >
                <span className="font-medium">Todos</span>
                <span className="text-xs text-gray-500 ml-2">
                  ({columnFilters[showDropdown]?.length || 0} valores)
                </span>
              </button>

              {columnFilters[showDropdown]?.map((val, i) => (
                <button
                  key={i}
                  onClick={() => {
                    onFilterChange(showDropdown, val);
                    setShowDropdown(null);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${
                    activeFilters[showDropdown] === val ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                >
                  <span className="truncate block">{val}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ----------- Context Menu --------------------------------- */}
        {contextMenuVisible && (
          <div
            ref={contextMenuRef}
            className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-48 z-50"
            style={{
              left: contextMenuPosition.x,
              top: contextMenuPosition.y
            }}
          >
            <button
              onClick={handleInsertRowAbove}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm text-gray-700"
            >
              <Plus size={16} />
              Adicionar linha acima
            </button>
          </div>
        )}
      </div>

      {/* ----------- Modal exclusão ---------------------------------- */}
      <ConfirmationModal
        isOpen={delModal.open}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta linha? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDelModal({open:false,row:null})}
        type="danger"
      />

      {/* ----------- Modal não pode adicionar linha --------------- */}
      <ConfirmationModal
        isOpen={showCannotAddRowModal}
        title="Ação não permitida"
        message="Não é possível adicionar novas linhas com filtros ativos ou busca em andamento. Limpe-os para continuar."
        confirmText="Entendi"
        onConfirm={() => setShowCannotAddRowModal(false)}
        onCancel={() => setShowCannotAddRowModal(false)}
        type="warning"
      />

      {/* ----------- Modal histórico --------------------------------- */}
      <CellHistoryModal
        isOpen={histModal.open}
        onClose={() => setHistModal({open:false,row:null,col:null})}
        columnName={histModal.col ?? ''}
        historyData={
          histModal.row !== null && histModal.col
            ? ((originalRows[histModal.row][histModal.col] as Cell)?.history ?? [])
            : []
        }
        currentValue={
          histModal.row !== null && histModal.col
            ? ((originalRows[histModal.row][histModal.col] as Cell)?.value ?? '')
            : ''
        }
      />
    </>
  );
};


export default SpreadsheetTable;
